import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiRefreshCw, FiEdit2, FiSave } from "react-icons/fi";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const API_BASE = "https://rentdoc-a0d223e1499e.herokuapp.com/api";

const AlertDialog = ({
  type = "error",
  title = "Alert",
  message = "Something happened.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  open = false,
  onClose = () => {},
  onConfirm = () => {},
}) => {
  const styles = {
    error: {
      icon: <ExclamationTriangleIcon className="size-6 text-red-400" />,
      iconBg: "bg-red-100",
      buttonBg: "bg-red-600 hover:bg-red-700",
    },
    success: {
      icon: <CheckCircleIcon className="size-6 text-green-600" />,
      iconBg: "bg-green-100",
      buttonBg: "bg-green-600 hover:bg-green-700",
    },
    info: {
      icon: <CheckCircleIcon className="size-6 text-blue-600" />,
      iconBg: "bg-blue-100",
      buttonBg: "bg-blue-600 hover:bg-blue-700",
    },
  };

  const { icon, iconBg, buttonBg } = styles[type] || styles.error;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/30 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0 sm:h-10 sm:w-10`}
              >
                {icon}
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{message}</p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
              <button
                type="button"
                onClick={onConfirm}
                className={`inline-flex w-full justify-center rounded-md ${buttonBg} px-3 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto`}
              >
                {confirmText}
              </button>
              {cancelText && (
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  {cancelText}
                </button>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default function BuildiumProperties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [modalForm, setModalForm] = useState({
    selectedOwnerBuildiumId: null,
    n1SignerType: "landlord",
    yearlyIncrease: 0,
    address: {
      AddressLine1: "",
      AddressLine2: "",
      City: "",
      State: "",
      PostalCode: "",
      Country: "",
    },
  });

  // ── Bulk selection state ──
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkIncrease, setBulkIncrease] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    confirmText: "OK",
    cancelText: null,
    onConfirm: () => {},
  });

  const showAlert = (
    type,
    title,
    message,
    confirmText = "OK",
    cancelText = null,
    onConfirm = () => {},
  ) => {
    setAlert({
      open: true,
      type,
      title,
      message,
      confirmText,
      cancelText,
      onConfirm,
    });
  };

  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");

    try {
      const res = await axios.get(`${API_BASE}/buildium/properties`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProperties(res.data || []);
    } catch (err) {
      console.error("Failed to fetch properties:", err);
      setError("Could not load properties. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const syncProperties = async () => {
    setSyncing(true);
    setError(null);
    const token = localStorage.getItem("token");

    try {
      const res = await axios.post(
        `${API_BASE}/buildium/properties/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data.success) {
        showAlert(
          "success",
          "Sync Successful",
          `Processed ${res.data.syncedActive || res.data.synced} active properties.`,
          "OK",
        );
        await fetchProperties();
      }
    } catch (err) {
      console.error("Sync failed:", err);
      showAlert(
        "error",
        "Sync Failed",
        err.response?.data?.message || "Failed to sync with Buildium.",
        "OK",
      );
    } finally {
      setSyncing(false);
    }
  };

  // ── Selection helpers ──
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allSelected =
    properties.length > 0 && selectedIds.size === properties.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected || someSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(properties.map((p) => p._id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkIncrease("");
  };

  const applyBulkIncrease = async () => {
    const value = parseFloat(bulkIncrease);
    if (isNaN(value) || value < 0) {
      showAlert(
        "error",
        "Invalid Value",
        "Enter a valid, non-negative percentage.",
        "OK",
      );
      return;
    }
    if (selectedIds.size === 0) return;

    setBulkSaving(true);
    const token = localStorage.getItem("token");
    const idsArray = Array.from(selectedIds);

    try {
      const res = await axios.put(
        `${API_BASE}/buildium/properties/bulk/yearly-increase`,
        { propertyIds: idsArray, yearlyIncrease: value },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Update local state for the affected properties without a full refetch
      setProperties((prev) =>
        prev.map((p) =>
          selectedIds.has(p._id) ? { ...p, yearlyIncrease: value } : p,
        ),
      );

      showAlert(
        "success",
        "Bulk Update Complete",
        `Updated yearly increase to ${value}% on ${res.data.modified} of ${res.data.matched} selected properties.`,
        "OK",
      );
      clearSelection();
    } catch (err) {
      console.error("Bulk update failed:", err);
      showAlert(
        "error",
        "Bulk Update Failed",
        err.response?.data?.message || "Could not apply the bulk update.",
        "OK",
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const openEdit = (prop) => {
    setEditingProperty(prop);
    setModalForm({
      selectedOwnerBuildiumId: prop.selectedOwnerBuildiumId || null,
      n1SignerType: prop.n1SignerType || "landlord",
      yearlyIncrease: prop.yearlyIncrease ?? 0,
      address: {
        AddressLine1: prop.address?.AddressLine1 || "",
        AddressLine2: prop.address?.AddressLine2 || "",
        City: prop.address?.City || "",
        State: prop.address?.State || "",
        PostalCode: prop.address?.PostalCode || "",
        Country: prop.address?.Country || "",
      },
    });
  };

  const closeEdit = () => {
    setEditingProperty(null);
  };

  const handleFormChange = (field, value) => {
    setModalForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setModalForm((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const saveAllEdits = async () => {
    if (!editingProperty) return;
    const id = editingProperty._id;
    const token = localStorage.getItem("token");

    try {
      await axios.put(
        `${API_BASE}/buildium/properties/${id}/landlord`,
        { selectedOwnerBuildiumId: modalForm.selectedOwnerBuildiumId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await axios.put(
        `${API_BASE}/buildium/properties/${id}/signer`,
        { n1SignerType: modalForm.n1SignerType },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await axios.put(
        `${API_BASE}/buildium/properties/${id}`,
        {
          yearlyIncrease: parseFloat(modalForm.yearlyIncrease),
          address: modalForm.address,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setProperties((prev) =>
        prev.map((p) =>
          p._id === id
            ? {
                ...p,
                selectedOwnerBuildiumId: modalForm.selectedOwnerBuildiumId,
                n1SignerType: modalForm.n1SignerType,
                yearlyIncrease: parseFloat(modalForm.yearlyIncrease),
                address: { ...p.address, ...modalForm.address },
              }
            : p,
        ),
      );

      showAlert(
        "success",
        "Saved",
        "All property settings updated successfully.",
        "OK",
      );
      closeEdit();
    } catch (err) {
      showAlert(
        "error",
        "Save Failed",
        "Could not update some settings.",
        "OK",
      );
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading properties...</div>;
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">
            Buildium Properties
          </h1>
          <p className="text-gray-500 mt-1">
            Manage your synced properties and settings
          </p>
        </div>
        <button
          onClick={syncProperties}
          disabled={syncing}
          className={`flex items-center gap-3 px-6 py-3 rounded-2xl text-white font-semibold transition-all ${syncing ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700 shadow-lg hover:shadow-xl"}`}
        >
          <FiRefreshCw className={`w-5 h-5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing with Buildium..." : "Sync with Buildium"}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl">
          {error}
        </div>
      )}

      {/* ── Bulk action bar (appears once something is selected) ── */}
      {selectedIds.size > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-4 p-5 bg-indigo-50 border border-indigo-200 rounded-2xl">
          <div className="text-sm font-semibold text-indigo-900">
            {selectedIds.size} propert{selectedIds.size === 1 ? "y" : "ies"}{" "}
            selected
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              value={bulkIncrease}
              onChange={(e) => setBulkIncrease(e.target.value)}
              placeholder="e.g. 2.5"
              className="w-32 bg-white border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-xl py-2 px-4 text-sm"
            />
            <span className="text-sm text-gray-500">%</span>
          </div>
          <button
            onClick={applyBulkIncrease}
            disabled={bulkSaving || bulkIncrease === ""}
            className={`px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all ${
              bulkSaving || bulkIncrease === ""
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            {bulkSaving ? "Applying..." : "Apply Yearly Increase"}
          </button>
          <button
            onClick={clearSelection}
            disabled={bulkSaving}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-white transition-all"
          >
            Clear Selection
          </button>
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-5 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
              </th>
              <th className="px-8 py-5 text-left text-sm font-semibold text-gray-600 uppercase tracking-widest">
                Property
              </th>
              <th className="px-8 py-5 text-left text-sm font-semibold text-gray-600 uppercase tracking-widest">
                Address
              </th>
              <th className="px-8 py-5 text-left text-sm font-semibold text-gray-600 uppercase tracking-widest">
                Property Manager
              </th>
              <th className="px-8 py-5 text-left text-sm font-semibold text-gray-600 uppercase tracking-widest">
                Landlord
              </th>
              <th className="px-8 py-5 text-left text-sm font-semibold text-gray-600 uppercase tracking-widest">
                N1 Signer
              </th>
              <th className="px-8 py-5 text-center text-sm font-semibold text-gray-600 uppercase tracking-widest">
                Yearly Increase
              </th>
              <th className="px-8 py-5 text-right text-sm font-semibold text-gray-600 uppercase tracking-widest">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {properties.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-8 py-16 text-center">
                  <div className="text-gray-400 text-xl">No properties yet</div>
                  <p className="text-sm text-gray-500 mt-2">
                    Sync with Buildium to get started
                  </p>
                </td>
              </tr>
            ) : (
              properties.map((prop) => {
                const manager = prop.rentalManager;
                const managerDisplay = manager ? (
                  <div className="text-sm">
                    <div className="font-semibold text-gray-900">
                      {manager.isCompany
                        ? manager.companyName
                        : `${manager.firstName || ""} ${manager.lastName || ""}`.trim() ||
                          "N/A"}
                    </div>
                    {manager.email && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {manager.email}
                      </div>
                    )}
                  </div>
                ) : (
                  "—"
                );

                const selectedOwner = prop.owners?.find(
                  (o) => o.buildiumId === prop.selectedOwnerBuildiumId,
                );
                const landlordDisplay = selectedOwner ? (
                  <div className="text-sm">
                    <div className="font-semibold text-gray-900">
                      {selectedOwner.isCompany
                        ? selectedOwner.companyName
                        : `${selectedOwner.firstName} ${selectedOwner.lastName}`}
                    </div>
                    {selectedOwner.email && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {selectedOwner.email}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-amber-500 text-sm font-medium">
                    Not selected
                  </span>
                );

                let signerDisplay = "—";
                if (
                  prop.n1SignerType === "representative" &&
                  prop.rentalManager
                ) {
                  const m = prop.rentalManager;
                  signerDisplay = (
                    <div className="text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        Representative
                      </span>
                      <div className="mt-1 font-medium">
                        {m.isCompany
                          ? m.companyName
                          : `${m.firstName} ${m.lastName}`}
                      </div>
                    </div>
                  );
                } else if (prop.n1SignerType === "landlord") {
                  signerDisplay = (
                    <div className="text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        Landlord
                      </span>
                      <div className="mt-1 font-medium">
                        {selectedOwner
                          ? selectedOwner.isCompany
                            ? selectedOwner.companyName
                            : `${selectedOwner.firstName} ${selectedOwner.lastName}`
                          : "Not selected"}
                      </div>
                    </div>
                  );
                }

                const yearlyDisplay =
                  prop.yearlyIncrease != null ? `${prop.yearlyIncrease}%` : "—";
                const isChecked = selectedIds.has(prop._id);

                return (
                  <tr
                    key={prop._id}
                    className={`hover:bg-gray-50 transition-colors group ${
                      isChecked ? "bg-indigo-50/50" : ""
                    }`}
                  >
                    <td className="px-6 py-6">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleSelected(prop._id)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-8 py-6">
                      <div className="text-base font-semibold text-gray-900">
                        {prop.name}
                      </div>
                    </td>
                    <td className="px-8 py-6 text-sm text-gray-600">
                      {prop.address?.AddressLine1 || "—"}
                    </td>
                    <td className="px-8 py-6">{managerDisplay}</td>
                    <td className="px-8 py-6">{landlordDisplay}</td>
                    <td className="px-8 py-6">{signerDisplay}</td>
                    <td className="px-8 py-6 text-center">
                      <div className="inline-flex items-center px-4 py-1.5 bg-orange-50 text-orange-700 rounded-2xl text-sm font-semibold">
                        {yearlyDisplay}
                      </div>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex items-center justify-end gap-4">
                        <button
                          onClick={() => openEdit(prop)}
                          className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 hover:border-indigo-500 hover:text-indigo-600 text-gray-700 rounded-2xl text-sm font-medium transition-all active:scale-95"
                        >
                          <FiEdit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <div className="text-xs text-gray-400 font-medium">
                          {prop.lastSynced
                            ? new Date(prop.lastSynced).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )
                            : "Never"}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        open={!!editingProperty}
        onClose={closeEdit}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="w-full max-w-2xl transform overflow-hidden rounded-3xl bg-white shadow-2xl">
              <div className="px-8 pt-8 pb-6">
                <div className="flex justify-between items-center mb-8">
                  <div>
                    <DialogTitle className="text-2xl font-semibold text-gray-900">
                      Edit {editingProperty?.name}
                    </DialogTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      Update settings for this property
                    </p>
                  </div>
                  <button
                    onClick={closeEdit}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-7 w-7" />
                  </button>
                </div>

                <div className="mb-10">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Property Address
                  </label>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={modalForm.address.AddressLine1}
                      onChange={(e) =>
                        handleAddressChange("AddressLine1", e.target.value)
                      }
                      placeholder="Address Line 1"
                      className="w-full bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-2xl py-3.5 px-5 text-base"
                    />
                    <input
                      type="text"
                      value={modalForm.address.AddressLine2}
                      onChange={(e) =>
                        handleAddressChange("AddressLine2", e.target.value)
                      }
                      placeholder="Address Line 2 (optional)"
                      className="w-full bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-2xl py-3.5 px-5 text-base"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={modalForm.address.City}
                        onChange={(e) =>
                          handleAddressChange("City", e.target.value)
                        }
                        placeholder="City"
                        className="bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-2xl py-3.5 px-5 text-base"
                      />
                      <input
                        type="text"
                        value={modalForm.address.State}
                        onChange={(e) =>
                          handleAddressChange("State", e.target.value)
                        }
                        placeholder="Province / State"
                        className="bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-2xl py-3.5 px-5 text-base"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={modalForm.address.PostalCode}
                        onChange={(e) =>
                          handleAddressChange("PostalCode", e.target.value)
                        }
                        placeholder="Postal Code"
                        className="bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-2xl py-3.5 px-5 text-base"
                      />
                      <input
                        type="text"
                        value={modalForm.address.Country}
                        onChange={(e) =>
                          handleAddressChange("Country", e.target.value)
                        }
                        placeholder="Country"
                        className="bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-2xl py-3.5 px-5 text-base"
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-10">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Primary Landlord
                  </label>
                  <select
                    value={modalForm.selectedOwnerBuildiumId || ""}
                    onChange={(e) =>
                      handleFormChange(
                        "selectedOwnerBuildiumId",
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                    className="w-full bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-2xl py-3.5 px-5 text-base"
                  >
                    <option value="">— Select a landlord —</option>
                    {editingProperty?.owners?.map((o) => (
                      <option key={o.buildiumId} value={o.buildiumId}>
                        {o.isCompany
                          ? o.companyName
                          : `${o.firstName} ${o.lastName}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-10">
                  <label className="block text-sm font-semibold text-gray-700 mb-4">
                    N1 Lease Signer
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    {["landlord", "representative"].map((type) => {
                      const isSelected = modalForm.n1SignerType === type;
                      const label =
                        type === "landlord"
                          ? "Landlord"
                          : "Property Manager (Representative)";
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => handleFormChange("n1SignerType", type)}
                          className={`p-6 rounded-3xl border-2 transition-all flex flex-col items-start ${isSelected ? "border-indigo-600 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}
                        >
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mb-4 ${isSelected ? "border-indigo-600 bg-indigo-600" : "border-gray-300"}`}
                          >
                            {isSelected && (
                              <div className="w-2 h-2 bg-white rounded-full" />
                            )}
                          </div>
                          <div className="font-semibold text-lg text-gray-900">
                            {label}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {type === "landlord"
                              ? "The selected primary landlord will sign"
                              : "The property manager will sign as representative"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Yearly Rent Increase (%)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={modalForm.yearlyIncrease}
                      onChange={(e) =>
                        handleFormChange(
                          "yearlyIncrease",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      className="w-full bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-2xl py-3.5 pl-5 pr-12 text-2xl font-semibold"
                    />
                    <div className="absolute right-6 top-1/2 -translate-y-1/2 text-2xl font-semibold text-gray-400">
                      %
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    This will be the default increase when generating new leases
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 px-8 py-6 flex justify-end gap-4 border-t border-gray-100 rounded-b-3xl">
                <button
                  onClick={closeEdit}
                  className="px-8 py-3.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-2xl transition"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAllEdits}
                  className="px-8 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-2xl flex items-center gap-2 transition shadow-lg"
                >
                  <FiSave className="w-4 h-4" />
                  Save All Changes
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      <AlertDialog
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText={alert.confirmText}
        cancelText={alert.cancelText}
        open={alert.open}
        onClose={closeAlert}
        onConfirm={() => {
          alert.onConfirm();
          closeAlert();
        }}
      />

      <div className="mt-8 text-center text-xs text-gray-400">
        Last updated: {new Date().toLocaleString()}
      </div>
    </div>
  );
}
